import { Calendar, Search, Train, Navigation, AlertTriangle, MapPin, Hash, List, Radio } from "lucide-react"
import DropdownCheckbox from "./DropdownCheckbox"


const ReportsFilters = ({
  dates,
  handleDateChange,
  handleSearch,
  coachType,
  direction,
  alarmType,
  location,
  sites,
  handleAllChange,
  handleIndividualChange,
  setCoachTypes,
  setDirection,
  setAlarmType,
  setLocation,
  trainNumber,
  rfid,
  coachNumber,
  handleTrainNumberChange,
  handleRfidChange,
  handleCoachNumberChange,
}) => {
  // Function to validate date range (max 3 months / 90 days)
  const validateDateRange = (fromDate, toDate) => {
    if (fromDate && toDate) {
      const from = new Date(fromDate)
      const to = new Date(toDate)
      const diffTime = Math.abs(to - from)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return diffDays <= 90
    }
    return true
  }
  const handleSearchWithValidation = () => {
    if (!validateDateRange(dates.fromDate, dates.toDate)) {
      alert("The date range cannot exceed 3 months (90 days).")
      return
    }
    handleSearch()
  }

  return (
    <div className="w-full max-w-full mx-auto bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="space-y-2">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
          <div className="lg:col-span-1 lg:row-span-2 bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 p-2 flex flex-col h-full">
            <div className="flex items-center gap-1.5 mb-2">
              <div className="p-1 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md shadow-sm">
                <Calendar className="w-3 h-3 text-white" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-gray-900">Date Range</h3>
                <p className="text-[10px] text-gray-500">Select period (max 3 months)</p>
              </div>
            </div>

            <div className="flex flex-col gap-1.5 mb-1">
              <div>
                <label htmlFor="fromDate" className="text-[10px] font-semibold text-gray-700 block mb-0.5">
                  From
                </label>
                <input
                  id="fromDate"
                  type="date"
                  name="fromDate"
                  value={dates.fromDate}
                  onChange={handleDateChange}
                  className="w-full px-1.5 py-1 text-[10px] border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                />
              </div>
              <div>
                <label htmlFor="toDate" className="text-[10px] font-semibold text-gray-700 block mb-0.5">
                  To
                </label>
                <input
                  id="toDate"
                  type="date"
                  name="toDate"
                  value={dates.toDate}
                  onChange={handleDateChange}
                  className="w-full px-1.5 py-1 text-[10px] border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-100 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white"
                />
              </div>
            </div>

            <button
              onClick={handleSearchWithValidation}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold py-1 rounded-md hover:from-blue-700 hover:to-blue-600 transition-all duration-300 flex items-center justify-center gap-1 text-[10px] shadow-sm hover:shadow-md mt-auto"
            >
              <Search className="w-3 h-3" />
              Search
            </button>
          </div>

          {/* First Row - Input Filters */}
          <div className="lg:col-span-3 grid grid-cols-1 lg:grid-cols-3 gap-2">
            {/* Train Number */}
            <div className="bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 p-2 flex flex-col">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="p-1 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-md shadow-sm">
                  <Hash className="w-3 h-3 text-white" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-gray-900">Train Number</h3>
                  <p className="text-[10px] text-gray-500">By ID</p>
                </div>
              </div>

              <div className="flex-1 flex flex-col mt-1">
                <input
                  id="trainNumber"
                  type="text"
                  value={trainNumber}
                  onChange={handleTrainNumberChange}
                  placeholder="e.g., 12345"
                  className="w-full px-1.5 py-1 text-[10px] border border-gray-200 rounded-md focus:ring-1 focus:ring-emerald-100 focus:border-emerald-500 transition-all duration-200 bg-gray-50 hover:bg-white placeholder-gray-400"
                />
              </div>
            </div>

            {/* Coach Number */}
            <div className="bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 p-2 flex flex-col">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="p-1 bg-gradient-to-br from-purple-500 to-purple-600 rounded-md shadow-sm">
                  <List className="w-3 h-3 text-white" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-gray-900">Coach Number</h3>
                  <p className="text-[10px] text-gray-500">By coach</p>
                </div>
              </div>

              <div className="flex-1 flex flex-col mt-1">
                <input
                  id="coachNumber"
                  type="text"
                  value={coachNumber}
                  onChange={handleCoachNumberChange}
                  placeholder="e.g., B1"
                  className="w-full px-1.5 py-1 text-[10px] border border-gray-200 rounded-md focus:ring-1 focus:ring-purple-100 focus:border-purple-500 transition-all duration-200 bg-gray-50 hover:bg-white placeholder-gray-400"
                />
              </div>
            </div>

            {/* RFID */}
            <div className="bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 p-2 flex flex-col">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="p-1 bg-gradient-to-br from-orange-500 to-orange-600 rounded-md shadow-sm">
                  <Radio className="w-3 h-3 text-white" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-gray-900">RFID</h3>
                  <p className="text-[10px] text-gray-500">By tag</p>
                </div>
              </div>

              <div className="flex-1 flex flex-col mt-1">
                <input
                  id="rfid"
                  type="text"
                  value={rfid}
                  onChange={handleRfidChange}
                  placeholder="e.g., RFID123456"
                  className="w-full px-1.5 py-1 text-[10px] border border-gray-200 rounded-md focus:ring-1 focus:ring-orange-100 focus:border-orange-500 transition-all duration-200 bg-gray-50 hover:bg-white placeholder-gray-400"
                />
              </div>
            </div>
          </div>

          {/* Second Row - Dropdown Filters */}
          <div className="lg:col-span-3 grid grid-cols-1 lg:grid-cols-4 gap-2">
            {/* Coach Type */}
            <div className="bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 p-2 flex flex-col">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="p-1 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-md shadow-sm">
                  <Train className="w-3 h-3 text-white" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-gray-900">Coach Type</h3>
                  <p className="text-[10px] text-gray-500">By type</p>
                </div>
              </div>

              <div className="flex-1">
                <DropdownCheckbox
                  label=""
                  options={[
                    { value: "wagon", label: "Wagon" },
                    { value: "lhb", label: "LHB" },
                    { value: "icf", label: "ICF" },
                  ]}
                  selectedOptions={Object.keys(coachType).filter((key) => key !== "all" && coachType[key])}
                  allSelected={coachType.all}
                  onSelectAll={() => handleAllChange(setCoachTypes, coachType, "Coach Type")}
                  onOptionChange={(type) => handleIndividualChange(type, setCoachTypes, coachType, "Coach Type")}
                  className="w-full text-[10px]"
                />
              </div>
            </div>

            {/* Direction */}
            <div className="bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 p-2 flex flex-col">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="p-1 bg-gradient-to-br from-teal-500 to-teal-600 rounded-md shadow-sm">
                  <Navigation className="w-3 h-3 text-white" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-gray-900">Direction</h3>
                  <p className="text-[10px] text-gray-500">By direction</p>
                </div>
              </div>

              <div className="flex-1">
                <DropdownCheckbox
                  label=""
                  options={[
                    { value: "up", label: "Up" },
                    { value: "down", label: "Down" },
                  ]}
                  selectedOptions={Object.keys(direction).filter((key) => key !== "all" && direction[key])}
                  allSelected={direction.all}
                  onSelectAll={() => handleAllChange(setDirection, direction, "Direction")}
                  onOptionChange={(dir) => handleIndividualChange(dir, setDirection, direction, "Direction")}
                  className="w-full text-[10px]"
                />
              </div>
            </div>

            {/* Alarm Type */}
            <div className="bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 p-2 flex flex-col">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="p-1 bg-gradient-to-br from-red-500 to-red-600 rounded-md shadow-sm">
                  <AlertTriangle className="w-3 h-3 text-white" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-gray-900">Alarm Type</h3>
                  <p className="text-[10px] text-gray-500">By alarms</p>
                </div>
              </div>

              <div className="flex-1">
                <DropdownCheckbox
                  label=""
                  options={[
                    { value: "alarms", label: "Alarms" },
                    { value: "warning", label: "Warning" },
                  ]}
                  selectedOptions={Object.keys(alarmType).filter((key) => key !== "all" && alarmType[key])}
                  allSelected={alarmType.all}
                  onSelectAll={() => handleAllChange(setAlarmType, alarmType, "Alarm Type")}
                  onOptionChange={(type) => handleIndividualChange(type, setAlarmType, alarmType, "Alarm Type")}
                  className="w-full text-[10px]"
                />
              </div>
            </div>

            {/* Location */}
            <div className="bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 p-2 flex flex-col">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="p-1 bg-gradient-to-br from-pink-500 to-pink-600 rounded-md shadow-sm">
                  <MapPin className="w-3 h-3 text-white" />
                </div>
                <div>
                  <h3 className="text-xs font-bold text-gray-900">Location</h3>
                  <p className="text-[10px] text-gray-500">By location</p>
                </div>
              </div>

              <div className="flex-1">
                <DropdownCheckbox
                  label=""
                  options={sites.map((site) => ({
                    value: site.siteName,
                    label: site.siteName,
                  }))}
                  selectedOptions={Object.keys(location).filter((key) => key !== "all" && location[key])}
                  allSelected={location.all}
                  onSelectAll={() => handleAllChange(setLocation, location, "Location")}
                  onOptionChange={(siteName) => handleIndividualChange(siteName, setLocation, location, "Location")}
                  className="w-full text-[10px]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}


export default ReportsFilters
